import React, { useEffect, useRef, useState, useCallback } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import { CONFIG } from "../config";
import { useLocation } from "react-router-dom";
import ContactForm from "./ContactForm"; // ✅ use your existing form
import "./PopupModal.css";

const IMAGES_BASE = CONFIG.IMAGES_BASE;
const imgURL = (name) => (name ? `${IMAGES_BASE}/${encodeURIComponent(name)}` : "");

/** Popup UI */
function PopupModal({ open, onClose, config }) {
  if (!open || !config) return null;

  const {
    image,
    title,
    message,
    showContactForm,
    useImageAsBackground,
    titleColor,
    messageColor,
  } = config;

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains("pm-backdrop")) onClose();
  };

  return (
    <div className="pm-backdrop" onClick={handleBackdropClick}>
      <div className={`pm-box ${useImageAsBackground ? "bg-mode" : ""}`}>
        {useImageAsBackground && image && (
          <div
            className="pm-bg"
            style={{ backgroundImage: `url(${imgURL(image)})` }}
          />
        )}

        <div className="pm-content">
          <button className="pm-close" onClick={onClose} aria-label="Close popup">
            ✕
          </button>

          {!useImageAsBackground && image && (
            <div className="pm-image-wrap">
              <img src={imgURL(image)} alt={title || "Popup"} />
            </div>
          )}

          {title && (
            <h2 className="pm-title" style={{ color: titleColor || "#111827" }}>
              {title}
            </h2>
          )}

          {message && (
            <p className="pm-text" style={{ color: messageColor || "#4b5563" }}>
              {message}
            </p>
          )}

          {showContactForm && (
            <div className="pm-form-wrap">
              {/* You can pass your actual contact config here instead of {} if needed */}
              <ContactForm config={{}} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Controller */
export default function PopupFromConfig() {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);

  const location = useLocation();
  const firstRouteRef = useRef(true);

  /** Subscribe to popupConfig */
  useEffect(() => {
    const r = ref(db, "popupConfig");
    const unsub = onValue(r, (snap) => {
      if (snap.exists()) setConfig(snap.val());
      else setConfig(null);
    });
    return () => unsub();
  }, []);

  const openPopup = useCallback(() => {
    if (!config?.triggers?.enable) return;
    setOpen(true);
  }, [config]);

  /** First visit trigger */
  useEffect(() => {
    if (!config?.triggers?.enable || !config.triggers.onVisit) return;
    const KEY = "popup_first_visit_shown";
    if (!sessionStorage.getItem(KEY)) {
      openPopup();
      sessionStorage.setItem(KEY, "1");
    }
  }, [config, openPopup]);

  /** Route change trigger */
  useEffect(() => {
    if (!config?.triggers?.enable || !config.triggers.onRouteChange) return;

    if (firstRouteRef.current) {
      firstRouteRef.current = false;
      return;
    }

    openPopup();
  }, [config, location?.pathname, openPopup]);

  /** Interval trigger */
  useEffect(() => {
    if (!config?.triggers?.enable || !config.triggers.intervalEnabled) return;

    const sec = Number(config.triggers.intervalSeconds || 0);
    if (!sec || sec < 5) return;

    const id = setInterval(openPopup, sec * 1000);
    return () => clearInterval(id);
  }, [config, openPopup]);

  if (!config?.triggers?.enable) return null;

  return (
    <PopupModal
      open={open}
      onClose={() => setOpen(false)}
      config={config}
    />
  );
}
